import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { actionOrderToCheckUnpaidOrders } from "../../actions/orderActions";
import MessageBox from "../../modules/shared/components/MessageBox";
import "./HomeScreen.css";
import "../../components/MessageProductFeature.css";
import Product from "../../components/Product";
import SearchResultsInfo from "../../components/SearchResultsInfo";
// import SuspenseFallback from "../../components/SuspenseFallback";
import LoadingContent from "../../components/LoadingContent";
import useIsMounted from "../../hooks/useIsMounted";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";
import { useScrollRestorationById } from "../../hooks/useScrollRestorationById";
import axios from "axios";
import HomeScreenSkeleton from "./HomeScreenSkeleton";

const HOME_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const HOME_CACHE_LIMIT = 4;
const HOME_CACHE_VERSION = "v9";

function getHomeCache(cacheKey) {
  try {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed?.savedAt || 0);

    if (
      !savedAt ||
      Date.now() - savedAt > HOME_CACHE_TTL_MS ||
      !Array.isArray(parsed?.items)
    ) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    if (!Array.isArray(parsed?.items) || parsed.items.length === 0) {
      return null;
    }

    return {
      items: parsed.items,
      hasMore: parsed.hasMore ?? true,
      nextPage: parsed.nextPage ?? 2,
    };
  } catch (err) {
    console.log("home cache parse error", err);
    return null;
  }
}

function calculateIsInstock(list) {
  if (!Array.isArray(list) || list.length === 0) return [];

  return list.map((item) => {
    let isInstock = false;

    if (item.isBundle) {
      const bundleItemsIds = Array.isArray(item.sideProducts)
        ? item.sideProducts
        : [];

      const bundleItems = list.filter((product) =>
        bundleItemsIds.includes(product._id),
      );

      const bundleItemsInStock = bundleItems.filter((bundleItem) => {
        const skuStock =
          Array.isArray(bundleItem.skus) &&
          bundleItem.skus.some((sku) => sku.countInStock > 0);

        return skuStock || bundleItem.defaultCountInStock > 0;
      });

      isInstock =
        bundleItemsIds.length > 0 &&
        bundleItemsInStock.length === bundleItemsIds.length;
    } else {
      const totalSkuStock = Array.isArray(item.skus)
        ? item.skus.reduce((a, c) => a + (c.countInStock || 0), 0)
        : 0;

      isInstock = (item.defaultCountInStock || 0) > 0 || totalSkuStock > 0;
    }

    return { ...item, isInstock };
  });
}

export default function HomeScreen(props) {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const cacheInitializedRef = useRef(false);

  // SCROLL RESTORATION LOGIC
  const mountRef = useRef(false);
  const isMounted = useIsMounted(mountRef);

  // PROPS COMING FROM APP
  const { createdFingerPrint, visitTime } = props;

  // REDUX STATE THAT HOLDS INFO IF THERE IS A PENDING ORDER OF CLIENT
  const checkIfThereIsApendingOrder = useSelector(
    (state) => state.checkIfThereIsApendingOrder,
  );
  const { isPending } = checkIfThereIsApendingOrder;

  // GET SEARCH KEYWORD FROM URL
  const keyword = props.match.params.keyword;

  // REDUX STORE THAT HOLDS SKU CART ITEMS
  const addOrRemoveSkuItemInCart = useSelector(
    (state) => state.addOrRemoveSkuItemInCart,
  );
  const { cartSkuItems } = addOrRemoveSkuItemInCart;

  const cacheKey = useMemo(() => {
    return `homeProductsCache:${HOME_CACHE_VERSION}:${keyword || "all"}`;
  }, [keyword]);

  const initialCache = useMemo(() => {
    return getHomeCache(cacheKey);
  }, [cacheKey]);

  // FIRST LOAD UI CONTROL
  const [isFirstLoad, setIsFirstLoad] = useState(
    !(initialCache?.items?.length > 0),
  );

  // INFINITE SCROLL LOGIC
  const {
    items: productsList,
    hasMore,
    //nextPage,
    isLoading,
    error,
    sentinelRef,
    loadMore,
    reset,
    hydrate,
  } = useInfiniteScroll({
    initialPage: 1,
    initialItems: initialCache?.items || [],
    initialHasMore: initialCache?.hasMore ?? true,
    initialNextPage: initialCache?.nextPage ?? null,
    getItemKey: (item) => item._id,
    fetcher: async ({ page, signal }) => {
      const { data } = await axios.get("/api/products", {
        params: {
          pageNumber: page,
          keyword: keyword || "",
        },
        signal,
      });

      return {
        items: data?.products || [],
        hasMore: page < (data?.pages || 0),
        nextPage: page + 1,
      };
    },
  });

  const calculatedProductsList = useMemo(() => {
    return calculateIsInstock(productsList || []);
  }, [productsList]);

  useScrollRestorationById({
    isMounted,
    items: productsList,
    hasMore,
    isLoading,
    loadMore,
  });

  useEffect(() => {
    if (!cacheInitializedRef.current) {
      cacheInitializedRef.current = true;
      return;
    }

    const cached = getHomeCache(cacheKey);

    if (cached?.items?.length > 0) {
      hydrate({
        items: cached.items || [],
        hasMore: cached.hasMore ?? true,
        nextPage: cached.nextPage ?? 2,
      });

      setIsFirstLoad(false);
      return;
    }

    reset();
    setIsFirstLoad(true);
  }, [cacheKey, hydrate, reset]);

  // IF THERE IS A PHONE ITEM IN LOCAL STORAGE -> check unpaid orders
  useEffect(() => {
    const phoneNumber = JSON.parse(localStorage.getItem("checkUnpaid"));
    if (phoneNumber) {
      dispatch(actionOrderToCheckUnpaidOrders(phoneNumber));
    }
  }, [history, dispatch, location]);

  // SHOW SEARCH RESULTS INFO
  const displaySearchInfo = location.pathname.substring(0, 4) === "/ara";

  // FIRST LOAD FINISHED
  useEffect(() => {
    if (!isLoading && isFirstLoad && productsList.length >= 0) {
      setIsFirstLoad(false);
    }
  }, [isLoading, isFirstLoad, productsList.length]);

  // SAVE CACHE
  useEffect(() => {
    if (!Array.isArray(productsList) || productsList.length === 0) return;

    try {
      const cachedItems = productsList.slice(0, HOME_CACHE_LIMIT);
      const cachedHasMore = hasMore || productsList.length > HOME_CACHE_LIMIT;

      localStorage.setItem(
        cacheKey,
        JSON.stringify({
          items: cachedItems,
          hasMore: cachedHasMore,
          nextPage: 2,
          savedAt: Date.now(),
        }),
      );
    } catch (err) {
      console.log("home cache save error", err);
    }
  }, [productsList, hasMore, cacheKey]);

  return (
    <>
      <Helmet>
        <title>beddo™ Anasayfa | Tüm beddo™ Ürünleri | Şimdi İncele</title>
        <meta
          name="description"
          content="Sağlıklı, rahat ve huzur veren beddo™ ürünlerine bir gözatın. Kullanıcı yararı gözetilerek üretilen beddo™ ürünleri tam 90 gün garantili. "
        />
        <meta name="keywords" content="beddo, beddo ürünleri" />
        <meta name="author" content="beddo™ Marketing Team" />
        <meta name="application-name" content="beddo™ ERP" />
      </Helmet>

      {isLoading && isFirstLoad ? (
        <HomeScreenSkeleton />
      ) : error ? (
        <MessageBox variant="danger">
          {error?.message || error?.toString() || "Bir hata oluştu"}
        </MessageBox>
      ) : (
        <>
          {displaySearchInfo && (
            <SearchResultsInfo
              loading={isLoading}
              error={error}
              products={calculatedProductsList}
            />
          )}

          <div className="products">
            {calculatedProductsList?.length > 0 &&
              calculatedProductsList.map((product, index) => {
                const isLast = index === calculatedProductsList.length - 1;

                const showHeadline =
                  index === 0 ||
                  calculatedProductsList[index - 1]?.homeHeadline !==
                    product.homeHeadline;

                return (
                  <React.Fragment key={product._id}>
                    {showHeadline && product.homeHeadline && (
                      <div className="home-category-headline">
                        {product.homeHeadline}
                      </div>
                    )}

                    <div
                      className="product-outer white-shadowbackground"
                      ref={isLast ? sentinelRef : null}
                    >
                      <div className="w100p">
                        {product.buyOneGetOneFree && (
                          <div className="w100p h2r col">
                            <div className="product-feature">
                              🎁 Hediyesi Var
                            </div>
                          </div>
                        )}
                      </div>

                      <div id={product._id}></div>

                      <Product
                        productsList={calculatedProductsList}
                        product={product}
                        cartSkuItems={cartSkuItems}
                        isPending={isPending}
                        createdFingerPrint={createdFingerPrint}
                        visitTime={visitTime}
                      />
                    </div>
                  </React.Fragment>
                );
              })}
          </div>

          {isLoading && !isFirstLoad && (
            <LoadingContent content={"Ürünler yükleniyor"} />
          )}
        </>
      )}
    </>
  );
}
